<script lang="ts">
    import Fa from 'svelte-fa'
    import { faBan, faSyncAlt, faArrowRight, faSquareCheck, faFilePdf, faFileExcel, faEdit, faPlusCircle, faUser } from '@fortawesome/free-solid-svg-icons'
    import { onMount } from 'svelte/internal'
    import dayjs from 'dayjs'
    import { GridBadge, GridBadgeClassEnum, GridButton, GridClass, GridCol, GridColTypeEnum, GridLine } from '../../_common/z/zgrid'
    import Content from '../../_common/content.svelte'
    import Popover from '../../_common/popover.svelte'
    import DateSelect from '../../_common/date-select.svelte'
    import Zgrid from '../../_common/z/zgrid.svelte'
    import LoadSpinner from '../../_common/load-spinner.svelte'
    import Zact from '../../_common/z/zact.svelte'
    import { TaxInvoiceEntity } from '../../../modules/posApi/tax-invoices/tax-invoice.entity'
    import TaxForm from './tax-form.svelte'
    import type { TaxParamEntity } from '../../../modules/posApi/tax-params/tax-param.entity'

    let show = false
    let entities: TaxInvoiceEntity[] = []
    let total = 0

    let dates: { from: Date; to: Date } = window.api.utils.dates.currentMonth()

    onMount(async () => {
        await loadEntities()
        setTimeout(() => loadTaxParams(), 10)
    })

    function dateChanged(event) {
        dates = event.detail
        loadEntities()
    }

    let taxParams: TaxParamEntity = null
    async function loadTaxParams() {
        taxParams = await window.api.pos.taxParams.getOne(window.store.company.companyId)
        console.log('taxParams', taxParams)
    }

    async function loadEntities() {
        if (!window.store.token.permissions.pos.taxInvoices?.read) return
        try {
            show = true
            const dt = {
                from: dates?.from || undefined,
                to: dates?.to || undefined,
                service: true,
            }
            entities = await window.api.pos.taxInvoices.getAll(dt)
            entities = await window.api.utils.utils.assignPersonsFromObject(entities)
            loadGrid()
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    function getBadgeLabel(status: number) {
        if (status === 105) return 'processando'
        if (status === 97) return 'rejeitada'
        if (status === 110) return 'autorizada'
        if (status === 106) return 'cancelando'
        if (status === 102) return 'cancelada'
        return 'rascunho'
    }

    function getBadgeColor(status: number) {
        if (status === 110) return GridBadgeClassEnum.badge_purple //
        if (status === 100) return GridBadgeClassEnum.badge_default //
        if (status === 97) return GridBadgeClassEnum.badge_danger //
        if (status === 105) return GridBadgeClassEnum.badge_success //
        if (status === 106) return GridBadgeClassEnum.badge_default //
        if (status === 102) return GridBadgeClassEnum.badge_alert //
        return GridBadgeClassEnum.badge_alert
    }

    let grid: GridClass = new GridClass()
    function loadGrid() {
        // grid
        grid = new GridClass()
        // grid head
        grid.title = 'Notas Fiscais Emitidas'
        grid.subtitle = ''
        grid.cols.push(new GridCol({ content: 'Data', type: GridColTypeEnum.datetime }))
        grid.cols.push(new GridCol({ content: 'Nome', type: GridColTypeEnum.string, maxLength: 33 }))
        grid.cols.push(new GridCol({ content: 'Status', type: GridColTypeEnum.badge }))
        grid.cols.push(new GridCol({ content: 'Mensagem de resultado' }))
        grid.cols.push(new GridCol({ content: 'Número', type: GridColTypeEnum.int }))
        grid.cols.push(new GridCol({ content: 'Valor', type: GridColTypeEnum.float_2 }))
        grid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        // grid lines
        for (const item of entities) {
            const btnPerson = new GridButton({
                callback: () => window.person('show', item.personId),
                icon: faUser,
                label: '',
            })
            const btnOpen = new GridButton({
                callback: () => editEntity(item.taxInvoiceId),
                icon: faEdit,
                label: 'Abrir',
            })
            const btnRPS = new GridButton({
                callback: () => getRPS(item.taxInvoiceId),
                icon: faFilePdf,
                label: 'RPS',
            })
            const btnPDF = new GridButton({
                callback: () => getPDF(item),
                icon: faFilePdf,
                label: 'NFSe',
            })
            const btnCancel = new GridButton({
                callback: () => cancelEntity(item.taxInvoiceId),
                icon: faBan,
                label: 'Cancelar',
            })
            const btnXML = new GridButton({
                callback: () => getXML(item),
                icon: faFileExcel,
                label: 'XML',
            })
            const btnSend = new GridButton({
                callback: () => sendEntity(item.taxInvoiceId),
                icon: faArrowRight,
                label: 'Transmitir',
            })
            const btnSync = new GridButton({
                callback: () => syncEntity(item.taxInvoiceId),
                icon: faSyncAlt,
                label: 'Sincronizar',
            })
            let buttons = []
            if (item.status === 100 || item.status === 97) buttons.push(btnSend)
            if (item.statusCancellation === 106) {
                buttons.push(btnSync)
            } else {
                if (item.status === 105) {
                    buttons.push(btnRPS)
                    buttons.push(btnSync)
                }

                if (item.status === 110) {
                    buttons.push(btnPDF)
                    buttons.push(btnXML)
                    if (item.statusCancellation !== 102 && item.statusCancellation !== 106) {
                        buttons.push(btnCancel)
                    }
                }
            }
            buttons.push(btnPerson)
            buttons.push(btnOpen)

            let line: GridLine = new GridLine()
            line.slots.push({ content: item.createdAt })
            line.slots.push({ content: item.person?.name || '[cliente não informado]' })
            // se cancelou
            if (item.statusCancellation === 102 || item.statusCancellation === 106) {
                line.slots.push({
                    content: new GridBadge({
                        label: getBadgeLabel(item.statusCancellation),
                        class: getBadgeColor(item.statusCancellation),
                    }),
                })
            } else {
                line.slots.push({
                    content: new GridBadge({
                        label: getBadgeLabel(item.status),
                        class: getBadgeColor(item.status),
                    }),
                })
            }
            let msg = item.responseMessage || ''
            if (item.statusCancellation === 106) msg += ' (cancelamento solicitado)'
            if (item.cancelResultMessage) msg += ' (cancelamento: ' + item.cancelResultMessage + ')'
            line.slots.push({ content: msg })
            line.slots.push({ content: window.api.pos.taxInvoices.nfseNumber(item) })
            line.slots.push({ content: item.services?.reduce((acc, el) => (acc += el.value.grossValue), 0) })
            line.slots.push({ content: buttons })
            grid.lines.push(line)
        }
    }

    let showForm = false
    let taxInvoiceId: string = null
    function editEntity(id: string) {
        taxInvoiceId = id
        showForm = true
    }

    async function sendEntity(taxInvoiceId) {
        try {
            show = true
            const result = await window.api.pos.taxInvoices.sendNFSe(taxInvoiceId)
            loadEntities()
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    async function syncEntity(taxInvoiceId) {
        try {
            show = true
            const result = await window.api.pos.taxInvoices.syncNFSe(taxInvoiceId)
            loadEntities()
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    async function cancelEntity(taxInvoiceId) {
        if (!confirm('Deseja cancelar esta NFSe na prefeitura?\nEsta ação é irreversível.')) return
        try {
            show = true
            const result = await window.api.pos.taxInvoices.cancelNFSe(taxInvoiceId)
            loadEntities()
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    async function getPDF(taxI: TaxInvoiceEntity) {
        try {
            show = true
            window.api.utils.utils.saveResponseAs(
                await window.api.pos.taxInvoices.getPDF(taxI.taxInvoiceId),
                'application/pdf',
                window.api.pos.taxInvoices.fileName(taxI, taxI.person?.name, 'pdf'),
            )
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    async function getRPS(taxInvoiceId) {
        try {
            show = true
            window.api.utils.utils.saveResponseAs(await window.api.pos.taxInvoices.getRPS(taxInvoiceId), 'application/pdf')
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    async function getXML(taxI: TaxInvoiceEntity) {
        try {
            show = true
            window.api.utils.utils.saveResponseAs(
                await window.api.pos.taxInvoices.getXML(taxI.taxInvoiceId),
                'application/xml',
                window.api.pos.taxInvoices.fileName(taxI, taxI.person?.name, 'xml'),
            )
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    function newTaxInvoice() {
        taxInvoiceId = null
        showForm = true
    }

    async function formChanged() {
        taxInvoiceId = null
        showForm = false
        await loadEntities()
    }

    function formCanceled() {
        taxInvoiceId = null
        showForm = false
    }

    let invoiceId = null
    let taxInvoice: TaxInvoiceEntity = null

    async function onInvoiceChanged(event) {
        loadEntities()
        taxInvoiceId = null
        invoiceId = null
    }
    let print = true
</script>

{#if showForm}
    <Popover
        title="Nota Fiscal"
        width="1300px"
        height="90vh"
        on:close={() => {
            showForm = false
        }}
    >
        <TaxForm {taxInvoiceId} on:change={formChanged} on:cancel={formCanceled} />
    </Popover>
{/if}

<Content footer={true}>
    <Zact>
        <DateSelect startFrom={dates.from} startTo={dates.to} on:change={dateChanged} />
        <div class="spacer h-mobile" />
        {#if window.store.token.permissions.pos.taxInvoices?.write}
            <button class="btn btn-primary" on:click={newTaxInvoice}><Fa icon={faPlusCircle} /> <span class="h-mobile"> Criar NFSe</span></button>
        {/if}
    </Zact>
    {#if taxParams && !taxParams?.nfseProduction}
        <div class="alert alert-warning mt-3">
            Atenção: Você está emitindo notas no modo de homologação. As notas emitidas não são válidas e devem ser usadas apenas para testar a integração junto
            ao emissor.
        </div>
    {/if}
    <Zgrid {grid} class="h-space" />
</Content>
<LoadSpinner {show} delay={0} />

<style>
</style>
